<template>
  <div
    class="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
    style="padding-bottom: calc(4rem + env(safe-area-inset-bottom))"
    @click.self="close"
  >
    <form
      class="w-full h-[85dvh] flex flex-col bg-white rounded-2xl shadow-xl overflow-hidden"
      @submit.prevent="isReviewing ? save() : review()"
    >
      <div class="flex items-center justify-between px-4 py-3 bg-dark-primary">
        <h2 class="text-lg font-semibold text-white">{{ isReviewing ? 'Confirm Changes' : 'Edit CRM' }}</h2>
        <button type="button" class="text-2xl leading-none text-white px-2" @click="isReviewing ? goBack() : close()">×</button>
      </div>

      <div v-if="isReviewing" class="flex-1 overflow-y-auto p-4 text-sm divide-y divide-gray-200">
        <div v-if="statusChanged" :class="reviewBlockClass">
          <div :class="reviewTitleClass">CRM Status</div>
          <div>{{ statusName(initialStatusId) }} → <span class="font-semibold">{{ statusName(statusId) }}</span></div>
        </div>
        <div v-if="nextActionChanged" :class="[reviewBlockClass, 'space-y-3']">
          <div v-if="nextAction !== initialNextAction">
            <div :class="reviewTitleClass">Next Action</div>
            <div>{{ initialNextAction || 'None' }} → <span class="font-semibold">{{ nextAction }}</span></div>
          </div>
          <div v-if="nextActionDueDate !== initialNextActionDueDate">
            <div :class="reviewTitleClass">Next Action Due Date</div>
            <div>{{ formatDate(initialNextActionDueDate) }} → <span class="font-semibold">{{ formatDate(nextActionDueDate) }}</span></div>
          </div>
        </div>
        <div v-if="hasDetail" :class="reviewBlockClass">
          <div :class="reviewTitleClass">CRM Details to Add</div>
          <ul class="mt-1 divide-y divide-gray-200">
            <li v-for="detail in details.filter(isFilled)" :key="detail.key" class="py-2">
              <div class="font-semibold">{{ actionName(detail.actionId) }}</div>
              <div class="text-gray-600">
                {{ detail.status === 'Done' ? `Done ${formatDate(detail.resolvedDate)}` : `Open, due ${formatDate(detail.dueDate)}` }}
              </div>
              <div v-if="detail.notes.trim()" class="whitespace-pre-wrap break-words text-gray-700">{{ detail.notes.trim() }}</div>
            </li>
          </ul>
        </div>
      </div>

      <div v-show="!isReviewing" class="flex-1 overflow-y-auto p-4 space-y-5">
        <section class="space-y-3">
          <h3 :class="sectionTitleClass">CRM Status</h3>
          <select v-model="statusId" :class="fieldClass(statusChanged)" :disabled="isLoadingOptions">
            <option :value="null" disabled>{{ isLoadingOptions ? 'Loading…' : '--' }}</option>
            <option v-for="status in statusOptions" :key="status.id" :value="status.id">{{ status.status_name }}</option>
          </select>
        </section>

        <section class="pt-4 space-y-3 border-t border-gray-200">
          <h3 :class="sectionTitleClass">Next Action</h3>
          <label class="block space-y-1">
            <div class="text-sm text-gray-600">Action</div>
            <select v-model="nextAction" :class="fieldClass(nextAction !== initialNextAction)">
              <option :value="null" disabled>--</option>
              <option v-for="option in nextActionChoices" :key="option" :value="option">{{ option }}</option>
            </select>
          </label>
          <label class="block space-y-1">
            <div class="text-sm text-gray-600">Due Date</div>
            <input v-model="nextActionDueDate" type="date" :class="fieldClass(nextActionDueDate !== initialNextActionDueDate)" />
          </label>
        </section>

        <section class="pt-4 space-y-3 border-t border-gray-200">
          <h3 :class="sectionTitleClass">CRM Detail</h3>
          <div
            v-for="(detail, index) in details"
            :key="detail.key"
            class="space-y-3"
            :class="{ 'pt-3 border-t border-dashed border-gray-300': index > 0 }"
          >
            <div class="space-y-1">
              <div class="flex items-center justify-between">
                <div class="text-sm text-gray-600">Event #{{ index + 1 }}</div>
                <button
                  type="button"
                  class="-my-1 px-2 text-xl leading-none text-gray-400"
                  aria-label="Remove CRM detail"
                  @click="removeDetail(detail.key)"
                >×</button>
              </div>
              <select v-model="detail.actionId" :class="inputClass" :disabled="isLoadingOptions">
                <option :value="null">{{ isLoadingOptions ? 'Loading…' : '-- None --' }}</option>
                <option v-for="action in actionOptions" :key="action.id" :value="action.id">{{ action.action_name }}</option>
              </select>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <label class="block space-y-1">
                <div class="text-sm text-gray-600">Status</div>
                <select v-model="detail.status" :class="inputClass">
                  <option value="Done">Done</option>
                  <option value="Open">Open</option>
                </select>
              </label>
              <label class="block space-y-1">
                <div class="text-sm text-gray-600">{{ detail.status === 'Done' ? 'Resolved Date' : 'Due Date' }}</div>
                <input v-if="detail.status === 'Done'" v-model="detail.resolvedDate" type="date" :class="inputClass" />
                <input v-else v-model="detail.dueDate" type="date" :class="inputClass" />
              </label>
            </div>
            <label class="block space-y-1">
              <div class="text-sm text-gray-600">Notes</div>
              <textarea v-model="detail.notes" rows="5" :class="inputClass"></textarea>
            </label>
          </div>
          <div class="flex justify-center">
            <button type="button" class="px-4 py-1.5 rounded-lg border border-dark-primary/30 text-sm text-dark-primary bg-white" @click="addDetail">+ Add CRM Detail</button>
          </div>
        </section>
      </div>

      <div class="px-4 py-2 space-y-2 border-t border-gray-200">
        <div v-if="errorMessage" class="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm p-3">{{ errorMessage }}</div>
        <div class="grid grid-cols-2 gap-2">
          <button v-if="isReviewing" type="button" class="py-2 rounded-md border border-gray-300 text-gray-700 disabled:opacity-50" :disabled="isSaving" @click="goBack">Back</button>
          <button v-else type="button" class="py-2 rounded-md border border-gray-300 text-gray-700" @click="close">Cancel</button>
          <button type="submit" class="py-2 rounded-md bg-dark-primary text-white disabled:opacity-50" :disabled="isSaving || isLoadingOptions">
            {{ isSaving ? 'Saving…' : isReviewing ? 'Confirm' : 'Save' }}
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
// Outside <script setup> so it lasts across modal opens, until the app reloads
let cachedStatusOptions = null
let cachedActionOptions = null
</script>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue';
import { useStore } from 'vuex';
import { getCrmStatuses } from '@/services/CrmStatus';
import { getActions } from '@/services/CrmAction';
import { addNewCrmStatus, updateCrmNextAction, addNewCrmAction } from '@/services/Company';
import { formatDate, getTodaysDateAsString } from '@/composables/formatDate';
const props = defineProps({
  company: Object
})

const emit = defineEmits(['close', 'crm-saved'])

const store = useStore()

const inputBaseClass = 'appearance-none w-full rounded-md border px-3 py-2 text-base'
const inputClass = `${inputBaseClass} border-gray-300 bg-white`
const changedInputClass = `${inputBaseClass} border-yellow-400 bg-yellow-100`

function fieldClass(changed) {
  return changed ? changedInputClass : inputClass
}
const sectionTitleClass = 'font-semibold text-gray-800'
const reviewBlockClass = 'py-3 first:pt-0 last:pb-0'
const reviewTitleClass = `${sectionTitleClass} text-base`

function currentStatus() {
  const campaign = props.company.campaign
  const statuses = (props.company.crm_statuses || []).filter(status => campaign && status.campaign && status.campaign.id == campaign.id)
  if (statuses.length === 0) {
    return null
  }
  const latest = statuses.reduce((acc, curr) => new Date(acc.created_on) > new Date(curr.created_on) ? acc : curr)
  return latest.crm_status
}
const initialStatus = currentStatus()

// Same choices as the desktop CRM Next Action cell (CRMNextActionCell.vue)
const nextActionOptions = [
  'None', 'Determine Next Action', 'Scott to Follow-Up', 'BA to Follow-Up', 'LG to Follow-Up', 'CB to Follow-Up',
  'Send Initial Email', 'Send Another Email', 'Send LinkedIn InMail', 'Send Email on Acquisition',
  'Research Correct Email', 'Call', 'Schedule Meeting', 'Wait on Response', 'Wait for Client Feedback', 'Meet',
  'Not Now', 'Leave Alone', 'Referral Source to Reach Out', 'Send 2nd Email', 'Send 3rd Email', 'Send 4th Email',
  'Cold Call 1', 'Cold Call 2', 'Call again', 'Periodic Check In', 'Submit IOI', 'Submit LOI',
]

// Refs so parts that save can become the new starting point, and a retry only resends what failed
const initialStatusId = ref(initialStatus ? initialStatus.id : null)
const initialNextAction = ref(props.company.crm_next_action || null)
const initialNextActionDueDate = ref(props.company.crm_next_action_due_date || '')

const statusId = ref(initialStatusId.value)
// Shows the current status straight away; the full list replaces it once loaded
const statusOptions = ref(cachedStatusOptions || (initialStatus ? [initialStatus] : []))
const isLoadingOptions = ref(true)

const nextAction = ref(initialNextAction.value)
const nextActionDueDate = ref(initialNextActionDueDate.value)

// Keeps an older next action that's no longer in the list from showing as blank
const nextActionChoices = initialNextAction.value && !nextActionOptions.includes(initialNextAction.value)
  ? [initialNextAction.value, ...nextActionOptions]
  : nextActionOptions

const actionOptions = ref(cachedActionOptions || [])

let nextDetailKey = 0
function emptyDetail() {
  return { key: nextDetailKey++, actionId: null, status: 'Done', resolvedDate: getTodaysDateAsString(), dueDate: '', notes: '' }
}
const details = ref([])

function addDetail() {
  details.value.push(emptyDetail())
}

function removeDetail(key) {
  details.value = details.value.filter(detail => detail.key !== key)
}

function isFilled(detail) {
  return detail.actionId !== null || detail.notes.trim() !== ''
}

const isSaving = ref(false)
const isReviewing = ref(false)
const errorMessage = ref('')

function statusName(id) {
  const status = statusOptions.value.find(option => option.id === id)
  return status ? status.status_name : 'None'
}

function actionName(id) {
  const action = actionOptions.value.find(option => option.id === id)
  return action ? action.action_name : ''
}

const statusChanged = computed(() => statusId.value !== initialStatusId.value)
const nextActionChanged = computed(() => nextAction.value !== initialNextAction.value || nextActionDueDate.value !== initialNextActionDueDate.value)
const hasDetail = computed(() => details.value.some(isFilled))
const hasChanges = computed(() => statusChanged.value || nextActionChanged.value || hasDetail.value)

async function loadOptions() {
  const token = store.getters['auth/showToken']
  const [statuses, actions] = await Promise.all([
    cachedStatusOptions || getCrmStatuses(token).catch(() => null),
    cachedActionOptions || getActions(token).then(response => response && response.data),
  ])
  if (statuses) {
    cachedStatusOptions = statuses
    statusOptions.value = statuses
  }
  if (actions) {
    cachedActionOptions = actions
    actionOptions.value = actions
  }
  if (!statuses || !actions) {
    errorMessage.value = "Couldn't load options. Check your connection and retry"
  }
  isLoadingOptions.value = false
}

function detailError() {
  for (const [index, detail] of details.value.entries()) {
    if (!isFilled(detail)) continue
    const label = `Event #${index + 1}`
    if (detail.actionId === null) return `Pick an event for ${label}`
    const date = detail.status === 'Done' ? detail.resolvedDate : detail.dueDate
    if (!date) return `Pick a ${detail.status === 'Done' ? 'resolved' : 'due'} date for ${label}`
  }
  return ''
}

function close() {
  if (isSaving.value) return
  if (hasChanges.value && !confirm('Exiting without saving will discard changes.')) return
  emit('close')
}

function goBack() {
  if (isSaving.value) return
  isReviewing.value = false
}

function review() {
  if (!hasChanges.value) {
    emit('close')
    return
  }
  errorMessage.value = detailError()
  if (errorMessage.value) return
  isReviewing.value = true
}

// Tries every part; crm-saved lists the parts that failed, which stay in the modal to retry
async function save() {
  const token = store.getters['auth/showToken']
  const requestOptions = { timeout: 15000, silent: true }
  const failed = []
  errorMessage.value = ''
  isSaving.value = true

  if (statusChanged.value) {
    const response = await addNewCrmStatus(props.company.id, statusId.value, token, requestOptions)
    if (response && response.status === 200) initialStatusId.value = statusId.value
    else failed.push('CRM Status')
  }

  if (nextActionChanged.value) {
    const data = {
      crm_next_action: nextAction.value,
      crm_next_action_due_date: nextActionDueDate.value || null,
    }
    const response = await updateCrmNextAction(props.company.id, data, token, requestOptions)
    if (response && response.status === 200) {
      initialNextAction.value = nextAction.value
      initialNextActionDueDate.value = nextActionDueDate.value
    } else failed.push('Next Action')
  }

  const savedKeys = []
  for (const detail of details.value) {
    if (!isFilled(detail)) continue
    const isDone = detail.status === 'Done'
    const data = {
      action_id: detail.actionId,
      status: detail.status,
      resolved_date: isDone ? detail.resolvedDate : null,
      due_date: isDone ? null : detail.dueDate,
      action_notes: detail.notes.trim() || null,
    }
    const response = await addNewCrmAction(props.company.id, data, token, requestOptions)
    if (response) savedKeys.push(detail.key)
    else failed.push(actionName(detail.actionId))
  }
  details.value = details.value.filter(detail => !savedKeys.includes(detail.key))

  isSaving.value = false
  if (failed.length > 0) {
    errorMessage.value = `Not saved: ${failed.join(', ')}. Check your connection and retry`
  }
  emit('crm-saved', failed)
}

loadOptions()
</script>
